import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TableColumnType, TableRowType } from '../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier } from '../../../grid/reduxStore/table.types';
import type { GridCellParams, GridRenderCellParams } from '@mui/x-data-grid-pro';
import PreviewEditableCellWrapper from './PreviewEditableCellWrapper';
import InputWithTooltips from '../Table/components/InputWithTooltips';
import { IsPricingCellWithRounding } from '../Table/helper';
import { isEligibleForStyling, applyColSpanRules } from '../Table/useApplyCustomRulesToColumns';
import { TipTapRenderCellPreview } from '../../TiptapEditor/components/TipTapRenderCellPreview';
import { DiscountSubtotalCell } from '../Table/components/DiscountSubtotalCell';
import { RowTypesIdentifier } from '../../../grid/reduxStore/table.types';
import type { EnhancedTableRowType } from '../../../grid/reduxStore/table.types';
import { usePreviewVariables } from '../../../../../providers/PreviewVariablesContext.tsx';
import { usePreviewContentVariables } from '../../../../../providers/PreviewContentVariablesContext.tsx';
import type { CurrencySettings } from '../../../../../services/documents/DocumentRepository.ts';

export function useApplyCustomRulesToColumnsInPreview({
  blockId,
  pricingDecimalPlaces,
  isTemplate = false,
  currencySettings,
}: {
  blockId: string;
  pricingDecimalPlaces: number;
  isTemplate?: boolean;
  currencySettings?: CurrencySettings;
}) {
  const { t } = useTranslation();

  const getSubtotalSuffix = (row: EnhancedTableRowType): string | undefined => {
    const frequency = row._pricingCalculation?.billingFrequency ?? row.billingFrequency ?? 'one-time';
    return frequency !== 'one-time' ? t(`pricing.billing_frequency.suffix.${frequency}`) : undefined;
  };

  const applyCustomRulesToColumnsInPreview = useCallback(
    (columns: TableColumnType[], updateRows: (updatedRow: TableRowType) => void, isDocumentLocked: boolean): TableColumnType[] => {
      let updatedColumns: TableColumnType[] = [...columns];
      updatedColumns = setPreviewCustomInputToDefaultColumn(updatedColumns, updateRows, isDocumentLocked, isTemplate);
      updatedColumns = applyColSpanRules(updatedColumns);

      return updatedColumns;
    },
    [t]
  );

  const setPreviewCustomInputToDefaultColumn = (
    columns: TableColumnType[],
    updateRows: (updatedRow: TableRowType) => void,
    isDocumentLocked: boolean,
    isTemplate?: boolean
  ): TableColumnType[] => {
    return columns.map((column) => ({
      ...column,
      renderEditCell: (params: GridCellParams) => {
        const { field, row } = params;
        if (IsPricingCellWithRounding(column.columnType, params.row.rowType)) {
          return (
            <InputWithTooltips
              onInputChange={(newValue) => {
                const updatedRow = { ...row, [field]: newValue };
                updateRows(updatedRow);
              }}
              params={params}
              blockId={blockId || ''}
              pricingDecimalPlaces={pricingDecimalPlaces}
            />
          );
        } else {
          return (
            <InputWithTooltips
              onInputChange={(newValue) => {
                const updatedRow = { ...row, [field]: newValue };
                updateRows(updatedRow);
              }}
              params={params}
              blockId={blockId || ''}
            />
          );
        }
      },
      renderCell: (params: GridRenderCellParams) => {
        const columnType = column.columnType;
        const isStylingApplicable = isEligibleForStyling(params.row.rowType, columnType);
        const previewRow = params.row as EnhancedTableRowType;
        const isDiscountSubtotalCellVisible =
          columnType === ColumnTypeIdentifier.SUBTOTAL &&
          params.row.rowType === RowTypesIdentifier.BODY &&
          previewRow.discount &&
          previewRow._pricingCalculation;

        if (isDiscountSubtotalCellVisible) {
          return (
            <DiscountSubtotalCell
              params={params}
              numberOfDecimalPlaces={pricingDecimalPlaces}
              documentCurrencySymbol={currencySettings?.currencySign}
            />
          );
        }

        if (IsPricingCellWithRounding(columnType, params.row.rowType)) {
          return (
            <PreviewEditableCellWrapper
              updateRows={updateRows}
              params={params}
              isDocumentLocked={isDocumentLocked}
              columnType={params.colDef['columnType'] as ColumnTypeIdentifier}
              pricingDecimalPlaces={pricingDecimalPlaces}
              documentCurrencySymbol={currencySettings?.currencySign}
              valueSuffix={
                columnType === ColumnTypeIdentifier.SUBTOTAL && params.row.rowType === RowTypesIdentifier.BODY
                  ? getSubtotalSuffix(previewRow)
                  : undefined
              }
            />
          );
        } else {
          if (isStylingApplicable && columnType !== ColumnTypeIdentifier.OPTIONAL) {
            return (
              <TipTapRenderCellPreview
                usePreviewVariables={isTemplate ? usePreviewContentVariables : usePreviewVariables}
                isPreview
                id={params.id}
                row={params.row}
                field={params.field}
                value={params.value}
                isTemplate={isTemplate}
              />
            );
          }
          return (
            <PreviewEditableCellWrapper
              updateRows={updateRows}
              params={params}
              isDocumentLocked={isDocumentLocked}
              columnType={params.colDef['columnType'] as ColumnTypeIdentifier}
              documentCurrencySymbol={currencySettings?.currencySign}
            />
          );
        }
      },
    }));
  };

  return { applyCustomRulesToColumnsInPreview };
}
